/* eslint-disable complexity */
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { FormattedNumber, useIntl } from 'react-intl';

import Image, { ImageLoadingType } from '../UI/Image/Image';
import openChatWidget from '../../utils/openChatWidget';
import { ButtonType } from '../../utils/phoneStatus/types';
import { DeviceType } from '../../types';
import isKiosk from '../../utils/isKiosk';
import { getLowestDualPricingMonthlyPrice } from '../../utils/getLowestMonthlyPrice';
import PhoneCompareButton from '../PhoneCompareButton/PhoneCompareButton';
import { ContentfulPhone, PageSectionFormField } from '../../ContentfulTypes';

import {
  ProductInfo,
  PriceContainer,
  Price,
  Disclaimer,
  StyledLink,
  StyledButton,
  ProductWrapper,
  CTAWrapper,
  ProductLink,
  BrandText,
  ModelText,
  Phone,
  BackImage,
  FrontImage,
  ProductLinkWrapper,
  PromoWrapper,
  PromoNumber,
  SkeletonTitle,
  SkeletonImage,
  SkeletonPrice,
  SkeletonPromo,
} from './styles';
import PhoneDisplayTag from './PhoneDisplayTag/PhoneDisplayTag';

export interface PhoneCardProps {
  phone: ContentfulPhone;
  location?: Record<string, any>;
  phoneLabels?: Array<PageSectionFormField>;
  buttonType?: ButtonType.Chat | ButtonType.Reserve | ButtonType.None;
  reservableColour?: string | null;
  reservableSize?: string | null;
  imageLoadingType?: ImageLoadingType;
  loading?: boolean;
}

const PhoneCard = ({
  phone,
  location,
  phoneLabels,
  buttonType = ButtonType.None,
  reservableColour = null,
  reservableSize = null,
  imageLoadingType,
  loading = false,
}: PhoneCardProps) => {
  const { locale } = useIntl();

  // If we have no offers for the phone then we don't display it
  if (!phone.phoneVariants?.length && !loading) return null;
    
  // Phone labels as object.
  const phoneLabelObject = phoneLabels?.reduce(
    (obj, item) => ({ ...obj, [item.contentful_id]: item.displayLabel }),
    {},
  );

  // Format phone name to separate and trim brand and model for display
  const brandDisplayName = phone.displayName?.split(' ', 1);
  const modelDisplayName = phone.displayName
    ?.replace(brandDisplayName, '')
    .trim();

  const isPhonesPage = location ? location.pathname.includes('phones') : false;
  const isPrepaidPhone = phone?.deviceType === DeviceType.PrepaidPhone;
  const isKioskMode = isKiosk();

  const monthlyPhonePrice = getLowestDualPricingMonthlyPrice({
    variants: phone.phoneVariants,
    locale,
  });

  if (loading) {
    return (
      <ProductWrapper data-testid="loading-product-wrapper">
        <Phone isKiosk={isKioskMode}>
          <SkeletonTitle />
        </Phone>
        <ProductLinkWrapper isKiosk={isKioskMode}>
          <ProductLink to="" isKiosk={isKioskMode} loading>
            <SkeletonImage />
          </ProductLink>
        </ProductLinkWrapper>
        <ProductInfo isKiosk={isKioskMode} isPhonesPage={isPhonesPage}>
          <SkeletonPrice />
        </ProductInfo>
        <PromoWrapper>
          <SkeletonPromo />
        </PromoWrapper>
        <CTAWrapper className={isPhonesPage && 'phonePage'} borderVisible={false} loading>
          <SkeletonPromo />
        </CTAWrapper>
      </ProductWrapper>
    );
  }
  return (
    <ProductWrapper className={isPhonesPage && 'phonePage'}>
      <PhoneDisplayTag
        phone={phone}
      />
      {/* IMAGE */}
      <Phone isKiosk={isKioskMode}>
        <StyledLink
          title={phone.displayName}
          className="phone-title"
          to={`/${locale}/phones/${phone.urlSlug}`}
        >
          <BrandText data-testid="brand-title">
            {brandDisplayName}
          </BrandText>
          <ModelText data-testid="model-name">
            {ReactHtmlParser(modelDisplayName)}
          </ModelText>
        </StyledLink>
        {/* Use override image if selected in CMS  */}
        {phone.featuredImageOverride ? (
          <ProductLinkWrapper isKiosk={isKioskMode}>
            <ProductLink
              to={`/${locale}/phones/${phone.urlSlug}`}
              title={phone.displayName}
              className="apple12Phone"
            >
              <Image
                image={phone.featuredPhoneBackImage}
                objectFit="contain"
                style={{ height: '145px' }}
                loading={imageLoadingType}
              />
            </ProductLink>
          </ProductLinkWrapper>
        ) : (
          // Otherwise treat it normally
          <ProductLinkWrapper isKiosk={isKioskMode}>
            <ProductLink
              to={`/${locale}/phones/${phone.urlSlug}`}
              title={phone.displayName}
              data-testid="product-image"
            >
              {' '}
              <BackImage
                image={phone.featuredPhoneBackImage}
                objectFit="contain"
                style={{ position: 'absolute', height: '145px' }}
              />
              <FrontImage
                image={phone.featuredPhoneFrontImage}
                objectFit="contain"
                style={{ position: 'absolute', height: '145px' }}
              />
            </ProductLink>
          </ProductLinkWrapper>
        )}

        {/* PRODUCT INFO */}
        <ProductInfo isKiosk={isKioskMode} isPhonesPage={isPhonesPage}>
          <div>
            {monthlyPhonePrice && (
              <PriceContainer>
                {ReactHtmlParser(phoneLabelObject?.['6i2XHnIuItS6v9nz34fzNU'])}
                {' '}
                <Price className="phoneLowestPrice">{monthlyPhonePrice}</Price>
                {' '}
                {ReactHtmlParser(phoneLabelObject?.aOC9eaI5Lh49zWX8rqb2Y)}
                {!isPrepaidPhone && (
                  <Disclaimer isKiosk={isKioskMode}>
                    {ReactHtmlParser(
                      phoneLabelObject?.['34y3ycugiqmC2my9dBKZKo'],
                    )}
                  </Disclaimer>
                )}
              </PriceContainer>
            )}
          </div>
        </ProductInfo>
      </Phone>
      {phone.highestPoints > 0 ? (
        <PromoWrapper applyBackground>
          {ReactHtmlParser(phoneLabelObject?.['44nERYyMP6YtSHRfYhSRen'])}
          {' '}
          <FormattedNumber value={phone.highestPoints as number}>
            {(num) => (
              <PromoNumber>
                {num}
                <sup>*</sup>
              </PromoNumber>
            )}
          </FormattedNumber>
        </PromoWrapper>
      ) : <PromoWrapper />}
      {/* BUTTONS */}
      <CTAWrapper className={isPhonesPage && 'phonePage'} borderVisible={!phone.highestPoints}>
        <PhoneCompareButton phone={phone} compareText={phoneLabelObject?.['2M0l02UslLxjP4JkRZyCS5']} />
        <StyledButton
          buttonColor="outline"
          buttonSize="xsmall"
          title={phone.displayName}
          route="internal"
          url={`/phones/${phone.urlSlug}`}
        >
          {/* eslint-disable-next-line dot-notation */}
          {ReactHtmlParser(phoneLabelObject?.['Kh4Onl6t9rUzDmKlpMZuM'])}
        </StyledButton>

        {buttonType === ButtonType.Chat && !isKioskMode && (
          <StyledButton
            as="button"
            type="button"
            buttonColor="red"
            buttonSize="xsmall"
            title={phone.displayName}
            route="chat-widget"
            onClick={openChatWidget}
            data-testid="product-chat-button"
          >
            {/* eslint-disable-next-line dot-notation */}
            {ReactHtmlParser(phoneLabelObject?.['s0KcOZGS0T6mi8QekzG3W'])}
          </StyledButton>
        )}
        {buttonType === ButtonType.Reserve && (
          <StyledButton
            buttonColor="red"
            buttonSize="xsmall"
            title={phone.displayName}
            route="internal"
            to={`/${locale}/phones/${phone.urlSlug}`}
            state={{ reserve: true, reservableColour, reservableSize }}
            data-testid="product-reserve-button"
          >
            {ReactHtmlParser(phoneLabelObject?.['1XcFFEqXysfEFZ6oE3RMiI'])}
          </StyledButton>
        )}
      </CTAWrapper>
    </ProductWrapper>
  );
};

export default PhoneCard;
